import { Plus } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { usePracticeHistoryState } from '@/hooks/mindspace/usePracticeHistory';
import type { PracticeMode, PracticeScope } from '@/lib/practice/session-types';
import { groupHistoryByDate } from '@/components/features/mindspace/practice/utils';
import { ActiveSessionRow } from './ActiveSessionRow';
import { HistoryRow } from './HistoryRow';
import {
  HistoryEmptyState,
  HistoryErrorState,
  HistorySkeleton,
} from './HistorySidebarStates';

type SidebarPhase = 'setup' | 'session' | 'results' | 'viewing';

interface PracticeHistorySidebarProps {
  /** Phase from the orchestrator. The "+ New session" CTA stays visible
   *  in every phase as a structural anchor but disables on `setup`
   *  (you're already there). */
  phase: SidebarPhase;
  /** Currently-viewed session id (from URL ?session=). Highlights the row. */
  selectedSessionId: string | null;
  /** Active-session metadata when phase === 'session'. Renders a styled
   *  "Practicing now" row pinned above the date groups. */
  activeSession: { scope: PracticeScope; mode: PracticeMode } | null;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
}

/**
 * Which body the list slot shows. A pinned active session always wins — the
 * user is mid-practice, and the row above says so. Otherwise: shimmer on the
 * first fetch rather than flashing the empty state; a FAILED read is its own
 * state, never "No sessions yet"; empty means the read succeeded with nothing.
 */
function historyBody(s: {
  isLoading: boolean;
  loadFailed: boolean;
  empty: boolean;
  active: boolean;
}): 'skeleton' | 'failed' | 'empty' | 'list' {
  if (s.active) return 'list';
  if (s.loadFailed) return 'failed';
  if (!s.empty) return 'list';
  return s.isLoading ? 'skeleton' : 'empty';
}

export function PracticeHistorySidebar({
  phase,
  selectedSessionId,
  activeSession,
  onSelectSession,
  onNewSession,
}: PracticeHistorySidebarProps) {
  const {
    sessions: history,
    isLoading,
    loadFailed,
    retry,
  } = usePracticeHistoryState();
  const groups = groupHistoryByDate(history);
  const body = historyBody({
    isLoading,
    loadFailed,
    empty: history.length === 0,
    active: !!activeSession,
  });

  return (
    <div className='flex h-full flex-col'>
      {/* New session CTA - matches Source panel's "Add Source" treatment
          (Button variant='outline' with subtle gray background). Stays
          visible in every phase as a visual anchor; disabled on setup. */}
      <div className='flex-shrink-0 px-3 pb-2 pt-3'>
        <Button
          onClick={onNewSession}
          disabled={phase === 'setup'}
          variant='outline'
          className='w-full border border-border bg-muted text-foreground transition-all duration-200 hover:bg-gray-900/[0.025] dark:border-border dark:bg-white/[0.04] dark:hover:bg-white/[0.07]'
        >
          <Plus className='mr-2 h-4 w-4' />
          New session
        </Button>
      </div>

      {/* Active session (pinned) + date-bucketed history */}
      <div className='min-h-0 flex-1 overflow-y-auto'>
        {activeSession && <ActiveSessionRow active={activeSession} />}

        {body === 'skeleton' ? (
          <HistorySkeleton />
        ) : body === 'failed' ? (
          <HistoryErrorState onRetry={retry} />
        ) : body === 'empty' ? (
          <HistoryEmptyState />
        ) : (
          <div className='space-y-3 px-2 pb-3 pt-2'>
            {groups.map(group => (
              <section key={group.label}>
                <h4 className='mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  {group.label}
                </h4>
                <ul className='space-y-0.5'>
                  {group.items.map(record => (
                    <li key={record.id}>
                      <HistoryRow
                        record={record}
                        selected={record.id === selectedSessionId}
                        onClick={() => onSelectSession(record.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
